<script>
import { mapActions, mapGetters } from 'vuex';
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iDrawer,
  iRadio,
  iTextInput,
  showToast,
} from '@inscribe/signal';
import { isValidEmail } from '@/utils/validation';
import * as userRoles from '@/constants/userRoles';
import IconText from '@/V2/components/IconText.vue';
import { title } from 'process';

export default {
  components: { iButton, IconText, iDrawer, iRadio, iTextInput },
  props: {
    user: {
      type: Object,
      default: null,
    },
    roles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      editingUser: null,
      newEmail: '',
      iButtonStyles,
      iButtonTypes,
    };
  },
  computed: {
    ...mapGetters({
      hasEditPermissions: 'isOrgAdmin',
    }),
    isEditMode() {
      return !!this.user?.email;
    },
    rolesOptions() {
      return this.roles.map((role) => {
        return { value: role.value, title: role.text, subtitle: role.subtext };
      });
    },
    isValidEmail() {
      return isValidEmail(this.newEmail);
    },
    isEmptyEmail() {
      return !this.newEmail || this.newEmail === '';
    },
  },
  watch: {
    user: function (newUser) {
      this.setEditingUser(newUser);
    },
  },
  mounted() {
    this.setEditingUser(this.user);
  },
  methods: {
    ...mapActions(['changeUserRole', 'inviteNewUser']),
    setEditingUser(user) {
      this.editingUser = { ...user };
      if (!user?.id) {
        this.editingUser.role = userRoles.USER_ROLE;
      }
    },
    getUserName(user) {
      return user.name && user.name !== '' ? user.name : user.email;
    },
    userIsCurrentUser(user) {
      return user?.id === this.$store.state.currentUser.uuid;
    },
    isRoleChangeDisabled(user) {
      return !this.hasEditPermissions || this.userIsCurrentUser(user);
    },
    setRole(role) {
      this.editingUser.role = role;
    },
    saveChanges() {
      if (this.isEditMode) {
        this.tryChangeUserRole(this.user?.id, this.editingUser?.role);
      } else {
        this.editingUser.email = this.newEmail;
        this.tryInviteNewUser();
      }
    },
    tryChangeUserRole(userId, role) {
      this.changeUserRole({ userId, role })
        .then((response) => {
          if (response.data.success) {
            this.$emit('users-edited');
            showToast({
              text: 'Role changed.',
            });
          }
        })
        .catch((error) => {
          showToast({
            title: 'There was a problem changing the user role',
            text: error?.response?.data?.message,
            isError: true,
          });
        });
    },
    tryInviteNewUser() {
      const data = new FormData();
      data.append('email', this.editingUser?.email);
      data.append('role', this.editingUser?.role);
      this.inviteNewUser(data)
        .then((response) => {
          this.$emit('users-edited');
          showToast({
            title: 'Invite sent',
            text: `An email will be sent to ${this.editingUser?.email} inviting them to create a password to log into this team.`,
          });
        })
        .catch((error) => {
          showToast({
            title: 'There was a problem sending your invite',
            text: error?.response?.data?.message,
            isError: true,
          });
        });
    },
  },
};
</script>
<template>
  <!-- create/edit user-->
  <i-drawer
    :visible="!!user"
    class="team-settings-user-panel"
    :title="isEditMode ? 'Edit User' : 'Invite User'"
    :subtitle="
      isEditMode
        ? 'Manage permissions for this user'
        : 'Invite a new user to your team with their work email'
    "
    @hide-drawer="$emit('hide-drawer')"
  >
    <!-- body -->
    <div class="user-data d-flex flex-column i-gap-11 i-mt-4">
      <!-- editing user block -->
      <div v-if="isEditMode" class="existing-user i-p-8 i-radius-2xl">
        <icon-text
          data-dd-action-name="team-settings-user-edit-link"
          icon="medium-user-single"
          :title="getUserName(user)"
          :subtitle="user.email"
        ></icon-text>
      </div>
      <div v-else>
        <i-text-input
          label="Email"
          :model-value="editingUser?.email"
          placeholder="teammate@example.com"
          type="email"
          :has-error="!isEmptyEmail && !isValidEmail"
          :show-hint-text="!isEmptyEmail && !isValidEmail"
          hint-text="A valid email is required"
          @update:model-value="newEmail = $event.toLowerCase()"
        ></i-text-input>
      </div>

      <!-- roles -->
      <div class="roles d-flex flex-column i-gap-4">
        <div class="role-label i-text-subheading-2 i-text-neutral-120">
          Role
        </div>
        <div class="roles-list">
          <i-radio
            :options="rolesOptions"
            :disabled="isRoleChangeDisabled(user)"
            :default-value="editingUser?.role"
            @option-selected="setRole"
          />
        </div>
      </div>
    </div>

    <!-- footer -->
    <template #footer>
      <div class="buttons-container d-flex flex-grow-1 i-gap-6">
        <i-button
          text="Cancel"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.SOLID_LIGHT"
          @button-click="$emit('hide-drawer')"
        ></i-button>
        <i-button
          :text="isEditMode ? 'Update' : 'Invite'"
          :disabled="
            (isEditMode && isRoleChangeDisabled()) ||
            (!isEditMode && !isValidEmail)
          "
          size="md"
          type="primary"
          subtype="strong"
          class="d-flex flex-grow-1"
          @button-click="saveChanges"
        ></i-button>
      </div>
    </template>
  </i-drawer>
</template>

<style lang="scss" scoped>
.team-settings-user-panel {
  .existing-user {
    border: 1px solid $light-neutral-60;
  }
}
</style>
